import React, { useState, useEffect, useRef } from "react";
import {
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { institutionAPI } from "../../../services/api";
import ManagePartnershipType from "./ManagePartnershipType";
import ManageCollaborationType from "./ManageCollaborationType";

export default function EditPartnershipInformation({
  isOpen,
  onClose,
  partnership,
  onSave,
}) {
  const initialState = {
    institution_id: "",
    partnership_name: "",
    partnership_type_id: "",
    collaboration_type: "",
    start_date: "",
    end_date: "",
    remarks: "",
  };

  const [formData, setFormData] = useState(initialState);
  const [institutions, setInstitutions] = useState([]);
  const [partnershipTypes, setPartnershipTypes] = useState([]);
  const [collaborationTypes, setCollaborationTypes] = useState([]);
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [isCollabModalOpen, setIsCollabModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  const [dropdownOpen, setDropdownOpen] = useState({
    institution: false,
    partnershipType: false,
    collaborationType: false,
  });

  // ---------------- FETCH DROPDOWNS ----------------
  useEffect(() => {
    const fetchDropdowns = async () => {
      setLoading(true);
      try {
        const [instRes, typeRes, collabRes] = await Promise.all([
          institutionAPI.getInstitutions({ per_page: "50" }),
          institutionAPI.getPartnerTypes({ per_page: "50" }),
          institutionAPI.getCollaborationTypes({ per_page: "50" }),
        ]);

        if (instRes.data.success) {
          setInstitutions(
            instRes.data.data.institutions.map((i) => ({
              id: i.institution_id.toString(),
              name: i.name,
            }))
          );
        }

        if (typeRes.data.success) {
          setPartnershipTypes(
            typeRes.data.data.partner_types.map((t) => ({
              id: t.id.toString(),
              name: t.name,
            }))
          );
        }

        if (collabRes.data.success) {
          setCollaborationTypes(
            collabRes.data.data.collaboration_types.map((c) => ({
              id: c.id.toString(),
              name: c.name,
            }))
          );
        }
      } catch (err) {
        console.error("Dropdown fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) fetchDropdowns();
  }, [isOpen]);

  // Populate form with selected partnership
  useEffect(() => {
    if (partnership) {
      setFormData({
        institution_id: partnership.institution?.institution_id?.toString() || "",
        partnership_name: partnership.partner_name || "",
        partnership_type_id: partnership.partner_type?.id?.toString() || "",
        collaboration_type: partnership.collaboration_type_id?.toString() || "",
        start_date: partnership.start_date?.split("T")[0] || "",
        end_date: partnership.end_date?.split("T")[0] || "",
        remarks: partnership.remarks || "",
      });
    }
  }, [partnership]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen({
          institution: false,
          partnershipType: false,
          collaborationType: false,
        });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.collaboration_type) {
      alert("Please select a collaboration type.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        institution_id: Number(formData.institution_id),
        partner_name: formData.partnership_name.trim(),
        partner_type_id: Number(formData.partnership_type_id),
        status: "active",
        agreement_title: collaborationTypes.find(
          (c) => c.id === formData.collaboration_type
        )?.name || "",
        collaboration_type_id: Number(formData.collaboration_type),
        agreement_date: formData.start_date,
        start_date: formData.start_date,
        end_date: formData.end_date,
        remarks: formData.remarks.trim(),
      };

      const res = await institutionAPI.updatePartnership(
        partnership.partnership_id,
        payload
      );

      if (res.data?.success) {
        onSave(res.data.data.partnership);
        onClose();
      }
    } catch (err) {
      console.error("Error updating partnership:", err.response?.data || err);
      alert(err.response?.data?.message || "Failed to update partnership");
    } finally {
      setLoading(false);
    }
  };

  const renderSelect = (name, value, options, key) => (
    <div className="relative w-full">
      <select
        name={name}
        value={value}
        required
        onChange={handleChange}
        onClick={() =>
          setDropdownOpen((prev) => ({ ...prev, [key]: !prev[key] }))
        }
        className="w-full border rounded-md px-3 py-2 pr-10 appearance-none focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        <option value="">Select</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        {dropdownOpen[key] ? (
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
        )}
      </div>
    </div>
  );

  const handleNewInstitutionType = () => setIsTypeModalOpen(true);
  const handleNewCollabType = () => setIsCollabModalOpen(true);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>

      <div
        ref={wrapperRef}
        className="relative bg-white rounded-lg w-full max-w-4xl shadow-lg p-6"
      >
        {/* Header */}
        <div className="flex justify-between items-center border-b pb-2 mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            Edit Partnership Information
          </h2>
          <button onClick={onClose}>
            <XMarkIcon className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Institution <span className="text-red-500">*</span>
              </label>
              {renderSelect("institution_id", formData.institution_id, institutions, "institution")}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Partnership Name <span className="text-red-500">*</span>
              </label>
              <input
                name="partnership_name"
                value={formData.partnership_name}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2"
                required
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Partnership Type */}
            <div>
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Partnership Type <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={handleNewInstitutionType}
                  className="ml-2 px-3 rounded text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 transition whitespace-nowrap"
                >
                  + New Partnership Type
                </button>
              </div>
              <div className="mt-1">
                {renderSelect(
                  "partnership_type_id",
                  formData.partnership_type_id,
                  partnershipTypes,
                  "partnershipType"
                )}
              </div>
            </div>

            {/* Collaboration Type */}
            <div>
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Type of Collaboration <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={handleNewCollabType}
                  className="ml-2 px-3 rounded text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 transition whitespace-nowrap"
                >
                  + New Collaboration Type
                </button>
              </div>
              <div className="mt-1">
                {renderSelect(
                  "collaboration_type",
                  formData.collaboration_type,
                  collaborationTypes,
                  "collaborationType"
                )}
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input type="date" name="start_date" value={formData.start_date} onChange={handleChange} className="w-full border rounded-md px-3 py-2" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date <span className="text-red-500">*</span>
              </label>
              <input type="date" name="end_date" value={formData.end_date} onChange={handleChange} className="w-full border rounded-md px-3 py-2" required />
            </div>
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remarks
            </label>
            <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows="3" className="w-full border rounded-md px-3 py-2" />
          </div>

          {/* Buttons */}
          <div className="flex justify-end pt-3 border-t mt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-md mr-2">
              Cancel
            </button>
            <button type="submit" disabled={loading} className={`px-5 py-2 rounded-md text-white ${loading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}>
              {loading ? "Updating..." : "Update"}
            </button>
          </div>
        </form>
      </div>

      <ManageCollaborationType
        isOpen={isCollabModalOpen}
        onClose={() => setIsCollabModalOpen(false)}
        onRefresh={() => {
          institutionAPI.getCollaborationTypes().then((res) => {
            if (res.data.success) {
              setCollaborationTypes(
                res.data.data.map((c) => ({
                  id: c.value.toString(),
                  name: c.label,
                }))
              );
            }
          });
        }}
      />

      <ManagePartnershipType
        isOpen={isTypeModalOpen}
        onClose={() => setIsTypeModalOpen(false)}
        onRefresh={() => {
          institutionAPI.getInstitutionTypes({ per_page: "50" }).then((res) => {
            if (res.data.success) {
              setPartnershipTypes(
                res.data.data.institution_types.map((t) => ({
                  id: t.id.toString(),
                  name: t.name,
                }))
              );
            }
          });
        }}
      />

    </div>
  );
}
